'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { supabase } from '@/lib/supabase';
import './landing.css';
import { 
  Zap, 
  Shield, 
  TrendingUp, 
  BrainCircuit, 
  Globe, 
  Activity, 
  Cpu, 
  LineChart,
  ChevronRight,
  ArrowRight,
  Languages,
  MousePointer2,
  Lock,
  BarChart3
} from 'lucide-react';

const TRANSLATIONS = {
  en: {
    nav_features: 'Features',
    nav_how_it_works: 'How It Works',
    nav_academy: 'Academy',
    nav_login: 'Login',
    nav_signup: 'Get Started',
    nav_dashboard: 'Go to Dashboard',
    hero_badge: 'Next-Gen AI Trading v2.0 Now Live',
    hero_title_1: 'Master the Markets with',
    hero_title_2: 'Autonomous AI Intelligence',
    hero_desc: 'Trade stocks, crypto, and gold with our hybrid AI engine that combines advanced technical indicators with real-time global sentiment analysis.',
    hero_btn_start: 'Launch Terminal',
    hero_btn_demo: 'View Live Demo',
    features_title: 'Engineered for Performance',
    features_desc: 'Everything you need to dominate the markets, powered by neural intelligence.',
    feat_autopilot_title: 'AI Autopilot',
    feat_autopilot_desc: 'Deploy autonomous agents that trade 24/7 based on your risk profile. No more emotional trading.',
    feat_sentiment_title: 'Sentiment Hunter',
    feat_sentiment_desc: 'Scans thousands of news sources and social signals every second to predict movements.',
    feat_hybrid_title: 'Hybrid Analysis',
    feat_hybrid_desc: 'Combines classical indicators with deep learning for ultra-accurate price predictions.',
    how_title: 'The Brain Behind Every Trade',
    how_step1_title: 'Data Ingestion',
    how_step1_desc: 'Collecting real-time market data, global news, and social sentiment from 500+ sources.',
    how_step2_title: 'Neural Analysis',
    how_step2_desc: 'Processing indicators (RSI, ATR, MACD) through our proprietary deep learning models.',
    how_step3_title: 'Smart Execution',
    how_step3_desc: 'Automatic trade execution and rebalancing with smart timing to maximize profits.',
    footer_text: 'Trade responsibly. AI is a tool to assist, not a guarantee of profit.'
  },
  th: {
    nav_features: 'ฟีเจอร์',
    nav_how_it_works: 'การทำงาน',
    nav_academy: 'ศูนย์เรียนรู้',
    nav_login: 'เข้าสู่ระบบ',
    nav_signup: 'เริ่มต้นใช้งาน',
    nav_dashboard: 'ไปที่แดชบอร์ด',
    hero_badge: 'ระบบเทรด AI เวอร์ชั่น 2.0 เปิดใช้งานแล้ว',
    hero_title_1: 'ครองตลาดการลงทุนด้วย',
    hero_title_2: 'สมองกล AI อัจฉริยะ',
    hero_desc: 'เทรดหุ้น คริปโต และทองคำ ด้วยระบบ Hybrid AI ที่รวบรวมเทคนิคอลขั้นสูงและบทวิเคราะห์ข่าวทั่วโลกแบบ Real-time',
    hero_btn_start: 'เปิดใช้งานระบบ',
    hero_btn_demo: 'ดูตัวอย่างพอร์ต',
    features_title: 'ออกแบบมาเพื่อความเป็นที่หนึ่ง',
    features_desc: 'ทุกเครื่องมือที่คุณต้องการเพื่อเป็นเจ้าแห่งตลาด ขับเคลื่อนด้วยระบบประสาทเทียม',
    feat_autopilot_title: 'AI Autopilot',
    feat_autopilot_desc: 'ส่ง Agent อัจฉริยะไปเทรดแทนคุณตลอด 24 ชม. ตามความเสี่ยงที่คุณรับได้ ตัดปัญหาเรื่องอารมณ์',
    feat_sentiment_title: 'Sentiment Hunter',
    feat_sentiment_desc: 'สแกนข่าวและโซเชียลทั่วโลกนับพันรายการในทุกวินาที เพื่อดักจังหวะล่วงหน้าก่อนตลาดวิ่ง',
    feat_hybrid_title: 'Hybrid Analysis',
    feat_hybrid_desc: 'ผสมผสานอินดิเคเตอร์คลาสสิกเข้ากับ Deep Learning เพื่อความแม่นยำสูงสุดในการทำนายราคา',
    how_title: 'เบื้องหลังความฉลาดของ AI',
    how_step1_title: 'การรวบรวมข้อมูล',
    how_step1_desc: 'ดึงข้อมูล Real-time จากข่าว ตลาด และโซเชียลจากแหล่งข้อมูลกว่า 500 แห่งทั่วโลก',
    how_step2_title: 'การประมวลผลอัจฉริยะ',
    how_step2_desc: 'นำอินดิเคเตอร์ (RSI, ATR, MACD) มาวิเคราะห์ผ่านโมเดล Deep Learning เฉพาะตัว',
    how_step3_title: 'การตัดสินใจที่เหนือกว่า',
    how_step3_desc: 'ส่งคำสั่งซื้อขายและ Rebalance พอร์ตในจังหวะที่ได้เปรียบที่สุดเพื่อกำไรสูงสุด',
    footer_text: 'การลงทุนมีความเสี่ยง AI เป็นเครื่องมือช่วยตัดสินใจ ไม่ใช่การการันตีกำไร'
  }
};

export default function LandingPage() {
  const [lang, setLang] = useState<'en' | 'th'>('en');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const t = TRANSLATIONS[lang];

  React.useEffect(() => {
    const checkUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setIsLoggedIn(!!session);
    };
    checkUser();
  }, []);

  return (
    <div className="landing-container">
      <div className="lp-background">
        <div className="lp-glow-1"></div>
        <div className="lp-glow-2"></div>
      </div>

      {/* Navbar */}
      <nav className="lp-nav">
        <div className="lp-logo">
          <BrainCircuit size={32} color="#8b5cf6" />
          Tradearaidee AI
        </div>
        
        <div className="lp-nav-links">
          <a href="#features" className="lp-nav-link">{t.nav_features}</a>
          <a href="#how-it-works" className="lp-nav-link">{t.nav_how_it_works}</a>
          <Link href="/guide" className="lp-nav-link">{t.nav_academy}</Link>
        </div>

        <div className="lp-auth-btns">
          <button className="lang-toggle" onClick={() => setLang(lang === 'en' ? 'th' : 'en')}>
            <Languages size={18} />
            <span>{lang.toUpperCase()}</span>
          </button>
          {isLoggedIn ? (
            <Link href="/overview" className="lp-btn-primary">{t.nav_dashboard}</Link>
          ) : (
            <>
              <Link href="/auth/login" className="lp-btn-outline">{t.nav_login}</Link>
              <Link href="/auth/signup" className="lp-btn-primary">{t.nav_signup}</Link>
            </>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <header className="lp-hero">
        <div className="lp-badge">
          <Zap size={14} />
          <span>{t.hero_badge}</span>
        </div>
        
        <h1>
          {t.hero_title_1}<br />
          <span>{t.hero_title_2}</span>
        </h1>
        
        <p>{t.hero_desc}</p>

        <div className="lp-auth-btns" style={{ marginTop: '20px', gap: '24px' }}>
          {isLoggedIn ? (
            <Link href="/overview" className="lp-btn-primary" style={{ padding: '16px 40px', fontSize: '1.1rem' }}>
              {t.nav_dashboard} <ArrowRight size={20} style={{ marginLeft: '10px' }} />
            </Link>
          ) : (
            <>
              <Link href="/auth/signup" className="lp-btn-primary" style={{ padding: '16px 40px', fontSize: '1.1rem' }}>
                {t.hero_btn_start} <ArrowRight size={20} style={{ marginLeft: '10px' }} />
              </Link>
              <Link href="/auth/login" className="lp-nav-link" style={{ fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                {t.hero_btn_demo} <ChevronRight size={20} />
              </Link>
            </>
          )}
        </div>

        <div className="lp-hero-image animate-fade-in" style={{ animationDelay: '0.4s' }}>
          <img src="/ai-terminal.png" alt="AI Terminal" />
          <div className="hero-stats-panel glass">
            <div className="stat-item">
              <div className="stat-label">AI WIN RATE</div>
              <div className="stat-value highlight">84.2%</div>
            </div>
            <div className="stat-item">
              <div className="stat-label">ACTIVE AGENTS</div>
              <div className="stat-value">12,842</div>
            </div>
            <div className="stat-item">
              <div className="stat-label">DAILY VOLUME</div>
              <div className="stat-value">฿50.4B</div>
            </div>
          </div>
        </div>
      </header>

      {/* How It Works - Detailed Section */}
      <section id="how-it-works" className="lp-how-it-works">
        <div className="lp-section-title">
          <div className="lp-badge">{t.nav_how_it_works}</div>
          <h2>{t.how_title}</h2>
        </div>

        <div className="how-steps">
          <div className="step-card glass">
            <div className="step-num">01</div>
            <Globe size={40} className="step-icon" />
            <h3>{t.how_step1_title}</h3>
            <p>{t.how_step1_desc}</p>
          </div>
          <div className="step-card glass">
            <div className="step-num">02</div>
            <Cpu size={40} className="step-icon" />
            <h3>{t.how_step2_title}</h3>
            <p>{t.how_step2_desc}</p>
          </div>
          <div className="step-card glass">
            <div className="step-num">03</div>
            <MousePointer2 size={40} className="step-icon" />
            <h3>{t.how_step3_title}</h3>
            <p>{t.how_step3_desc}</p>
          </div>
        </div>

        <div className="engine-visual glass">
            <div className="visual-content">
                <BarChart3 size={48} color="var(--accent-primary)" />
                <div className="visual-text">
                    <h4>Neural Engine v2.4</h4>
                    <p>Processing 1.2M market signals per second across 3 continents.</p>
                </div>
            </div>
            <div className="visual-badges">
                <span className="v-badge"><Lock size={12}/> Encrypted</span>
                <span className="v-badge"><Activity size={12}/> 99.9% Uptime</span>
                <span className="v-badge"><Zap size={12}/> Millisecond Execution</span>
            </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="lp-features">
        <div className="lp-section-title">
          <div className="lp-badge">{t.nav_features}</div>
          <h2>{t.features_title}</h2>
          <p>{t.features_desc}</p>
        </div>

        <div className="lp-features-grid">
          <div className="lp-feature-card glass">
            <div className="lp-feature-icon"><Zap size={30} /></div>
            <h3>{t.feat_autopilot_title}</h3>
            <p>{t.feat_autopilot_desc}</p>
          </div>

          <div className="lp-feature-card glass">
            <div className="lp-feature-icon"><Globe size={30} /></div>
            <h3>{t.feat_sentiment_title}</h3>
            <p>{t.feat_sentiment_desc}</p>
          </div>

          <div className="lp-feature-card glass">
            <div className="lp-feature-icon"><LineChart size={30} /></div>
            <h3>{t.feat_hybrid_title}</h3>
            <p>{t.feat_hybrid_desc}</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="lp-footer">
        <div className="lp-logo" style={{ justifyContent: 'center', marginBottom: '24px' }}>
          <BrainCircuit size={32} color="#8b5cf6" />
          Tradearaidee AI
        </div>
        <p className="footer-warning">{t.footer_text}</p>
        <p style={{ color: '#94a3b8', marginBottom: '40px' }}>&copy; 2026 Tradearaidee AI. All rights reserved.</p>
        <div style={{ display: 'flex', justifyContent: 'center', gap: '32px' }}>
          <a href="#" className="lp-nav-link">Privacy Policy</a>
          <a href="#" className="lp-nav-link">Terms of Service</a>
          <a href="#" className="lp-nav-link">Contact</a>
        </div>
      </footer>
    </div>
  );
}
